import { tableObject, nameMapping } from "./tableData";

// ---------------------------------------------------------------------------
// Tiered licensing access control.
//
// A single `tier` field on each license doc ("parents" | "regular" | "premium")
// is the source of truth for what a logged-in user may open. Admins have no
// license and are promoted to the "admin" pseudo-tier (see effectiveTier).
//
//   parents  -> a fixed 24-activity subset, no Academy
//   regular  -> all activities, no Academy
//   premium  -> everything, including Academy
//   admin    -> everything (pseudo-tier, not stored on a license)
// ---------------------------------------------------------------------------

// The 24 activities the Parents tier may open. These strings MUST match the
// exact `tableObject` spelling byte-for-byte (the string drives the PDF path);
// a typo silently drops the row from Parents' view. tierAccess.test.js asserts
// this Set is a subset of the flattened tableObject names.
export const parentActivities = new Set([
  // Coding
  "Pointing",
  "Arrows",
  "Memory Dots",
  "Angels in the Snow",
  "Ball Bounce",
  "B, d, p, and q",
  // Visualisation
  "Tangrams",
  "Tangrams 2",
  "Flashing Words",
  "Dot to Dot Patterns",
  "Block Patterns",
  "Advanced Tangrams",
  // Eye Movements
  "Koala Copy",
  "Picture Memory",
  "Join the Circus",
  "Letter LeapFrog Croaky Charts",
  "Letter LeapFrog Songs",
  "Letter Leap Frog Tales",
  // Visually Directed Fine Motor
  "Maze",
  "Follow the Line",
  "Origami",
  "Pegboard Match",
  "Trail Blazers",
  "Advanced Mazes",
]);

// The tiers that can be assigned to a seat (the "admin" pseudo-tier is not
// assignable). Single source of truth for tier pickers across the account views.
export const TIERS = ["parents", "regular", "premium"];

// Grandfather default: grants/licenses minted before the `tier` field existed
// resolve to this. Mirrored server-side in functions/index.js (claimAccess).
export const DEFAULT_TIER = "premium";

// Whether a tier may open a given activity (by its tableObject name).
export function canAccessActivity(tier, activityName) {
  if (tier === "regular" || tier === "premium" || tier === "admin") {
    return true;
  }
  if (tier === "parents") {
    return parentActivities.has(activityName);
  }
  return false;
}

// Whether a tier may view the Academy.
export function canAccessAcademy(tier) {
  return tier === "premium" || tier === "admin";
}

// Resolve the effective tier for gating. Admins have no license (so
// authUserData.tier is null/undefined) and must be promoted to "admin" or they
// get locked out of every gated screen.
export function effectiveTier(authUserData) {
  if (authUserData?.userType === "admin") {
    return "admin";
  }
  return authUserData?.tier ?? null;
}

// ---------------------------------------------------------------------------
// Forward pdfPath -> activityName map.
//
// nameMapping is not generally invertible, so we never reverse the path
// heuristically. Instead we derive the canonical PDF path for every activity
// using the SAME build logic as activity-table.jsx (tableClickHandler), then
// look the loaded `pdf` up here. Keep this in sync with that handler.
// ---------------------------------------------------------------------------
export function activityPdfPath(category, activityName) {
  const shortName =
    activityName in nameMapping
      ? nameMapping[activityName]
      : activityName.replaceAll(" ", "");
  const cat = category.replaceAll(" ", "");
  return "/" + cat + "/" + shortName + ".pdf";
}

export const pdfPathToActivity = (() => {
  const map = {};
  Object.keys(tableObject).forEach((category) => {
    Object.keys(tableObject[category]).forEach((level) => {
      tableObject[category][level].forEach((activity) => {
        map[activityPdfPath(category, activity)] = activity;
      });
    });
  });
  return map;
})();

// Resolve the activity name for a loaded PDF path. Returns null when the path
// is not a known activity (e.g. landing pages, help PDFs) — callers should not
// gate those.
export function activityNameForPdf(pdfPath) {
  return pdfPathToActivity[pdfPath] ?? null;
}
